// Pack/unpack an EncryptedBlob to/from a portable byte string.
//
// Layout (v1):
//   magic     4   "PWVT"
//   version   1   uint8
//   iters     4   uint32 BE
//   salt     16   bytes
//   nonce    12   bytes
//   ct      var   ciphertext (includes 16-byte GCM auth tag at the end)
//
// Only one KDF/cipher pair is supported per version. If those change, bump
// version and add a new branch — never reinterpret an old file.

import { CURRENT_VERSION, NONCE_BYTES, SALT_BYTES } from '../crypto/vault-crypto.js';

const MAGIC = new Uint8Array([0x50, 0x57, 0x56, 0x54]); // "PWVT"
const HEADER_BYTES = 4 /*magic*/ + 1 /*version*/ + 4 /*iters*/ + SALT_BYTES + NONCE_BYTES;

// Accepted range for the (unauthenticated) iteration count in a blob header.
// The floor keeps a downgraded file from being cheap to crack; the ceiling
// bounds the work an untrusted file can make us do. Current vaults use 600k.
export const MIN_ITERATIONS = 100_000;
export const MAX_ITERATIONS = 10_000_000;

export function packBlob(blob) {
  if (!blob || blob.version !== CURRENT_VERSION) {
    throw new Error(`packBlob: unsupported version ${blob?.version}`);
  }
  if (!(blob.salt instanceof Uint8Array) || blob.salt.byteLength !== SALT_BYTES) {
    throw new Error('packBlob: salt missing or wrong length');
  }
  if (!(blob.nonce instanceof Uint8Array) || blob.nonce.byteLength !== NONCE_BYTES) {
    throw new Error('packBlob: nonce missing or wrong length');
  }
  if (!(blob.ciphertext instanceof Uint8Array)) {
    throw new Error('packBlob: ciphertext missing');
  }

  const out = new Uint8Array(HEADER_BYTES + blob.ciphertext.byteLength);
  let o = 0;
  out.set(MAGIC, o); o += 4;
  out[o++] = blob.version;
  const view = new DataView(out.buffer);
  view.setUint32(o, blob.iterations, false); o += 4;
  out.set(blob.salt, o); o += SALT_BYTES;
  out.set(blob.nonce, o); o += NONCE_BYTES;
  out.set(blob.ciphertext, o);
  return out;
}

export function unpackBlob(bytes) {
  if (!(bytes instanceof Uint8Array) || bytes.byteLength < HEADER_BYTES) {
    throw new Error('unpackBlob: input too short');
  }
  for (let i = 0; i < MAGIC.length; i++) {
    if (bytes[i] !== MAGIC[i]) throw new Error('unpackBlob: bad magic');
  }
  let o = 4;
  const version = bytes[o++];
  if (version !== CURRENT_VERSION) {
    throw new Error(`unpackBlob: unsupported version ${version}`);
  }
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const iterations = view.getUint32(o, false); o += 4;
  // The header is outside the AES-GCM tag, so this value is attacker-settable
  // on any blob we did not write. PBKDF2 must run to completion before GCM can
  // reject the file, so an unbounded count (up to 2^32-1) freezes the tab for
  // hours on import or on joining a tampered cloud vault.
  if (iterations < MIN_ITERATIONS || iterations > MAX_ITERATIONS) {
    throw new Error(`unpackBlob: iterations out of range (${iterations})`);
  }
  const salt = bytes.slice(o, o + SALT_BYTES); o += SALT_BYTES;
  const nonce = bytes.slice(o, o + NONCE_BYTES); o += NONCE_BYTES;
  const ciphertext = bytes.slice(o);
  return {
    version,
    kdf: 'PBKDF2-SHA256',
    iterations,
    salt,
    nonce,
    ciphertext,
  };
}
